import { Component, inject } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import {
  filter,
  map,
  switchMap,
  distinctUntilChanged,
  take,
  tap,
  EMPTY,
  from,
} from 'rxjs';

import { HeaderComponent } from '@shared/components/nav/header/header.component';
import { MenuComponent } from '@shared/components/nav/menu/menu.component';
import { FooterComponent } from '@shared/components/nav/footer/footer.component';

import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@services/auth/auth.service';
import { CommonService } from '@services/common/common.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, MenuComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  // Services
  oktaStateService = inject(OktaAuthStateService);
  oktaAuth = inject(OKTA_AUTH);
  authService = inject(AuthService);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  commonService: CommonService = inject(CommonService);
  title = 'mySoftProApp';
  showHeader = false;
  url = '';
  userData: any;
  userName = '';

  // Derived auth$ from Okta
  private isAuthenticated$ = this.oktaStateService.authState$.pipe(
    map((s: AuthState) => !!s?.isAuthenticated),
    distinctUntilChanged(), // only on changes
  );

  receivedData: string = '';
  isLoggedIn: boolean = false;
  isAuthenticated: boolean = false;
  onUnauthenticatedPage: boolean = false;

  // Main Menu Variables
  showHostedPortal: boolean = false;
  showMySoftProUsers: boolean = false;
  showOnlineBillPay: boolean = false;

  constructor() {
    this.isAuthenticated$
      .pipe(
        takeUntilDestroyed(),
        tap((isAuth) => (this.isAuthenticated = isAuth)),
        switchMap((isAuth) => {
          if (!isAuth) {
            // Clear per-auth session UI data
            this.userData = undefined;
            this.userName = '';
            // No work to do
            return EMPTY;
          }

          // Wait for your app data layer to be ready just once
          return this.authService.isLoaded$.pipe(
            filter((v) => v != null),
            take(1),
            switchMap((loaded) => {
              if (!loaded) {
                // Make it an Observable
                return from(this.authService.initializeApplication());
              }
              return EMPTY;
            }),
            switchMap(() => from(this.authService.getUserDataFromSession())),
          );
        }),

        tap((userData: any) => {
          if (userData) {
            this.userData = userData;
            this.userName = `${userData.firstName} ${userData.lastName}`;
            this.syncMenuVisibility();
          }
        }),
      )
      .subscribe();

    // Header visibility
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((e) => e instanceof NavigationEnd),
        tap((e: any) => (this.url = e.urlAfterRedirects ?? e.url)),
        map(() => this.getDeepestChild(this.route)),
        map((snap) => snap?.data ?? {}),
      )
      .subscribe((data) => {
        this.showHeader = !data['hideHeader'];
      });
    // OktaAuthGuard will handle protected routes and trigger sign-in when needed.
  }

  private getDeepestChild(route: ActivatedRoute) {
    let r = route.snapshot;
    while (r.firstChild) r = r.firstChild;
    return r;
  }

  private syncMenuVisibility() {
    this.showHostedPortal = this.authService.showHostedPortal();
    this.showMySoftProUsers = this.authService.showMySoftProUsers();
    this.showOnlineBillPay = this.authService.showOnlineBillPay();
  }

  protected initializeData() {
    // We are on a page that requires authentication and the user is authenticated.
    //let appUserData = await this.authService.getUserDataFromSession();
    this.userName = `${this.userData.firstName} ${this.userData.lastName}`;

    // User can access the hosted portal if
    // 1. They have a system role
    // 2. They are an administrator of any account that has the hosted portal enabled.

    // Show the Hosted Portal Users link
    this.showHostedPortal = this.authService.showHostedPortal();

    // Show the MySoftPro Users link
    this.showMySoftProUsers = this.authService.showMySoftProUsers();

    // Show the Online Bill Pay link
    this.showOnlineBillPay = this.authService.showOnlineBillPay();
  }
}
